/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import React from 'react';

import Styles from '../../css/LoanDefaultCodes.module.css';
import LoadingModal from '../modals/LoadingModal';
import LoanInformationStyles from '../../css/LoanInformation.module.css';
import { createTheme, ThemeProvider, IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import EnvelopeWithBox from '../EnvelopeWithBox';
import ButtonBorderWrapper from '../ButtonBorderWrapper';
import { useLDCContext } from '../../contexts/LoanDefaultCodesContext';
import ErrorsModal from '../loan-default-codes/Errors';
import Buttons from '../loan-default-codes/Buttons';
import MainLDCCol from '../loan-default-codes/MainLDCColumn';
import FhlmcCol from '../loan-default-codes/FHLMCCol';
import ActionsCol from '../loan-default-codes/ActionsCol';
import GnmaCol from '../loan-default-codes/GNMACol';
import { useAuthContext } from '../../contexts/AuthContext';

const theme = createTheme({
  palette: {
    text: {
      disabled: 'black',
    },
  },
  typography: {
    fontFamily: `"Poppins", sans-serif`,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
  },
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          whiteSpace: 'normal',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          whiteSpace: 'normal',
        },
        root: {
          width: '100%',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          width: '100%',
        },
      },
    },
  },
});

const LoanDefaultCodes = () => {
  const { LDCStatus, LDCUIStatuses, LDCFunctions, noInvestorCols } = useLDCContext();
  const { fullAccess } = useAuthContext();

  const { getBKStatusAbbrev, handleEdit } = LDCFunctions;
  const { editingInProcess, showEdit, showSaveCancel } = LDCUIStatuses;
  const { loading } = LDCStatus;

  const editingInProcessInfo = () => {
    return (
      editingInProcess && (
        <div className={Styles.editingInProcessInfo}>
          {'Please click outside the field you are currently editing in order to save or cancel.'}
        </div>
      )
    );
  };

  getBKStatusAbbrev();

  const mainContent = () => {
    return (
      <div className={`${LoanInformationStyles.loanInfoTable} ${Styles.marginadd}`}>
        {loading && <LoadingModal open={true} />}

        <div className={noInvestorCols ? LoanInformationStyles.centered : LoanInformationStyles.leftColumn}>
          <MainLDCCol></MainLDCCol>
        </div>

        {!noInvestorCols && (
          <div className={LoanInformationStyles.rightColumn}>
            <FhlmcCol></FhlmcCol>
            <ActionsCol></ActionsCol>
            <GnmaCol></GnmaCol>
          </div>
        )}
      </div>
    );
  };

  const blockName = () => {
    if (showEdit) {
      return (
        <>
          Loan Default Codes{' '}
          <IconButton title={'Edit'} className={LoanInformationStyles.EnvelopeWithBoxButton} onClick={handleEdit}>
            <Edit />
          </IconButton>
        </>
      );
    } else {
      return 'Loan Default Codes';
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {showSaveCancel ? (
        <ButtonBorderWrapper>
          <EnvelopeWithBox blockName={blockName()}>
            {mainContent()}
            {editingInProcessInfo()}
            <ErrorsModal></ErrorsModal>
          </EnvelopeWithBox>
          <Buttons></Buttons>
        </ButtonBorderWrapper>
      ) : (
        <EnvelopeWithBox blockName={blockName()}>
          {mainContent()}
          {fullAccess && <ErrorsModal></ErrorsModal>}
        </EnvelopeWithBox>
      )}
    </ThemeProvider>
  );
};

export default LoanDefaultCodes;
